import { useState, useEffect } from 'react';

type Theme = 'light' | 'dark' | 'auto';
type ColorScheme = 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'indigo' | 'pink' | 'teal';

interface ThemeConfig {
  theme: Theme;
  colorScheme: ColorScheme;
}

const colorSchemes = {
  blue: {
    name: 'Mavi',
    primary: '#3b82f6',
    primaryDark: '#2563eb',
    secondary: '#1e40af',
    accent: '#60a5fa',
    cssVars: {
      '--color-primary': '59 130 246',
      '--color-primary-dark': '37 99 235',
      '--color-secondary': '30 64 175',
      '--color-accent': '96 165 250'
    }
  },
  green: {
    name: 'Yeşil',
    primary: '#10b981',
    primaryDark: '#059669',
    secondary: '#047857',
    accent: '#34d399',
    cssVars: {
      '--color-primary': '16 185 129',
      '--color-primary-dark': '5 150 105',
      '--color-secondary': '4 120 87',
      '--color-accent': '52 211 153'
    }
  },
  purple: {
    name: 'Mor',
    primary: '#8b5cf6',
    primaryDark: '#7c3aed',
    secondary: '#6d28d9',
    accent: '#a78bfa',
    cssVars: {
      '--color-primary': '139 92 246',
      '--color-primary-dark': '124 58 237',
      '--color-secondary': '109 40 217',
      '--color-accent': '167 139 250'
    }
  },
  orange: {
    name: 'Turuncu',
    primary: '#f97316',
    primaryDark: '#ea580c',
    secondary: '#c2410c',
    accent: '#fb923c',
    cssVars: {
      '--color-primary': '249 115 22',
      '--color-primary-dark': '234 88 12',
      '--color-secondary': '194 65 12',
      '--color-accent': '251 146 60'
    }
  },
  red: {
    name: 'Kırmızı',
    primary: '#ef4444',
    primaryDark: '#dc2626',
    secondary: '#b91c1c',
    accent: '#f87171',
    cssVars: {
      '--color-primary': '239 68 68',
      '--color-primary-dark': '220 38 38',
      '--color-secondary': '185 28 28',
      '--color-accent': '248 113 113'
    }
  },
  indigo: {
    name: 'İndigo',
    primary: '#6366f1',
    primaryDark: '#4f46e5',
    secondary: '#4338ca',
    accent: '#818cf8',
    cssVars: {
      '--color-primary': '99 102 241',
      '--color-primary-dark': '79 70 229',
      '--color-secondary': '67 56 202',
      '--color-accent': '129 140 248'
    }
  },
  pink: {
    name: 'Pembe',
    primary: '#ec4899',
    primaryDark: '#db2777',
    secondary: '#be185d',
    accent: '#f472b6',
    cssVars: {
      '--color-primary': '236 72 153',
      '--color-primary-dark': '219 39 119',
      '--color-secondary': '190 24 93',
      '--color-accent': '244 114 182'
    }
  },
  teal: {
    name: 'Deniz Mavisi',
    primary: '#14b8a6',
    primaryDark: '#0d9488',
    secondary: '#0f766e',
    accent: '#2dd4bf',
    cssVars: {
      '--color-primary': '20 184 166',
      '--color-primary-dark': '13 148 136',
      '--color-secondary': '15 118 110',
      '--color-accent': '45 212 191'
    }
  }
};

export const useTheme = () => {
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(() => {
    // Check localStorage first, then default to dark blue
    const savedTheme = localStorage.getItem('theme') as Theme;
    const savedColorScheme = localStorage.getItem('colorScheme') as ColorScheme;
    return {
      theme: savedTheme || 'light',
      colorScheme: savedColorScheme || 'blue'
    };
  });

  useEffect(() => {
    const root = window.document.documentElement;
    
    const applyTheme = (config: ThemeConfig) => {
      // Remove existing theme classes
      root.classList.remove('light', 'dark');
      
      // Remove existing color scheme classes first
      Object.keys(colorSchemes).forEach(scheme => {
        root.classList.remove(`theme-${scheme}`);
      });
      
      // Apply theme
      if (config.theme === 'auto') {
        // Use system preference
        const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.classList.add(systemPrefersDark ? 'dark' : 'light');
      } else {
        root.classList.add(config.theme);
      }

      // Apply color scheme CSS variables
      const scheme = colorSchemes[config.colorScheme];
      Object.entries(scheme.cssVars).forEach(([property, value]) => {
        root.style.setProperty(property, value);
      });

      // Add current color scheme class
      root.classList.add(`theme-${config.colorScheme}`);
    };

    applyTheme(themeConfig);
    localStorage.setItem('theme', themeConfig.theme);
    localStorage.setItem('colorScheme', themeConfig.colorScheme);

    // Listen for system theme changes when in auto mode
    if (themeConfig.theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme(themeConfig);
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeConfig]);

  const toggleTheme = () => {
    setThemeConfig(prev => ({
      ...prev,
      theme: prev.theme === 'light' ? 'dark' : 'light'
    }));
  };

  const setThemeMode = (newTheme: Theme) => {
    setThemeConfig(prev => ({
      ...prev,
      theme: newTheme
    }));
  };

  const setColorScheme = (newColorScheme: ColorScheme) => {
    setThemeConfig(prev => ({
      ...prev,
      colorScheme: newColorScheme
    }));
  };

  const updateThemeConfig = (config: ThemeConfig) => {
    setThemeConfig(config);
  };

  return {
    theme: themeConfig.theme,
    colorScheme: themeConfig.colorScheme,
    colorSchemes,
    toggleTheme,
    setThemeMode,
    setColorScheme,
    updateThemeConfig,
    isDark: themeConfig.theme === 'dark' || (themeConfig.theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches),
    currentColorScheme: colorSchemes[themeConfig.colorScheme]
  };
};
